/**
 * Status Category Section
 *
 * Configure attributes for a specific status category. Rows live in the
 * configuration store (statusBuilderConfiguration) and serialize into the
 * AdvancedSettings payload as the PascalCase StatusBuilder section — see
 * shared/api/pipeex/statusBuilderWire.ts. Each row emits one PCF attribute
 * for every element matching the category's phase; the value is either a
 * constant or the element's actual Revit parameter value.
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  TableBody,
  TableCell,
  Typography,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { CaptionText } from '../../../../shared/ui/typography';
import SearchableDropdown, { DropdownOption } from '../../../../shared/ui/forms/SearchableDropdown';
import TableHeaderField from '../../../../shared/ui/table/TableHeaderField';
import { useRevitParameterOptions } from '../../../../shared/hooks/useRevitParameterOptions';
import {
  useAddStatusAttribute,
  useRemoveStatusAttribute,
  useStatusBuilderConfiguration,
  useUpdateStatusCategory,
  useUpdateStatusAttribute,
} from '../../../../shared/stores/useConfigurationStore';
import type {
  StatusPhaseKind,
  StatusValueSource,
} from '../../../../shared/types/config/status-builder.types';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import { mappingOverviewTooltips } from '../../../../shared/config/mappingHelpCatalog';
import {
  CompactAddAction,
  MappingEditorHeader,
  MappingRow,
  ScopeEditorTableVariant,
  scopeEditorDensity,
  getScopeEditorActionButtonSx,
  getScopeEditorDropdownSx,
} from '../../../../shared/ui/mapping/ScopeEditorPrimitives';
import { getSemanticSurfaceTokens } from '../../../../theme/theme';
import { WorkspaceSection } from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';

interface StatusCategorySectionProps {
  categoryId: string;
}

const COLUMN_WIDTHS = {
  pcfAttribute: '32%',
  source: '20%',
  value: '42%',
  actions: 28,
};

const SOURCE_OPTIONS: DropdownOption[] = [
  {
    value: 'Constant',
    label: 'Direct value',
    tooltip: 'Write this exact value to the PCF attribute for every element in the matching Revit phase.',
    group: 'Source',
  },
  {
    value: 'RevitParameter',
    label: 'Revit parameter',
    tooltip: 'Read the value from each matching Revit element when the PCF file is exported.',
    group: 'Source',
  },
];

const PCF_ATTRIBUTE_OPTIONS: DropdownOption[] = [
  {
    value: 'STATUS',
    label: 'STATUS',
    tooltip: 'Status written for the element, such as DOTTED-DIMENSIONED or ERECTION',
    group: 'Status',
  },
  {
    value: 'MATERIAL_LIST',
    label: 'MATERIAL_LIST',
    tooltip: 'Controls whether and how the element appears in the material list, such as EXCLUDE',
    group: 'Status',
  },
  {
    value: 'STATUS-CODE',
    label: 'STATUS-CODE',
    tooltip: 'Numeric code used by the downstream status workflow',
    group: 'Status',
  },
  {
    value: 'DEMOLITION-DATE',
    label: 'DEMOLITION-DATE',
    tooltip: 'Planned demolition date',
    group: 'Status',
  },
  {
    value: 'INSTALL-DATE',
    label: 'INSTALL-DATE',
    tooltip: 'Installation date',
    group: 'Status',
  },
];

const PHASE_KIND_OPTIONS: DropdownOption[] = [
  {
    value: 'Created',
    label: 'Phase created',
    tooltip: 'Use the Revit Phase Created value to decide whether an element belongs to this status category',
    group: 'Revit phase dimension',
  },
  {
    value: 'Demolished',
    label: 'Phase demolished',
    tooltip: 'Use the Revit Phase Demolished value to decide whether an element belongs to this status category',
    group: 'Revit phase dimension',
  },
];

const StatusCategorySection: React.FC<StatusCategorySectionProps> = ({ categoryId }) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const { categories } = useStatusBuilderConfiguration();
  const addStatusAttribute = useAddStatusAttribute();
  const updateStatusCategory = useUpdateStatusCategory();
  const updateStatusAttribute = useUpdateStatusAttribute();
  const removeStatusAttribute = useRemoveStatusAttribute();
  const { options: revitParamOptions } = useRevitParameterOptions({
    preferredParameterType: 'Instance',
  });
  const category = categories.find((candidate) => candidate.id === categoryId);

  const [filters, setFilters] = useState({ pcfAttribute: '', value: '' });

  const handleFilterChange = (field: 'pcfAttribute' | 'value', value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const visibleAttributes = useMemo(() => {
    const attributes = category?.attributes ?? [];
    return attributes.filter((attribute) => {
      const matchesPcf = attribute.pcfAttribute
        .toLowerCase()
        .includes(filters.pcfAttribute.toLowerCase());
      const matchesValue = attribute.value
        .toLowerCase()
        .includes(filters.value.toLowerCase());
      return matchesPcf && matchesValue;
    });
  }, [category, filters]);

  const phaseKind: StatusPhaseKind = category?.phaseKind === 'Demolished'
    ? 'Demolished'
    : 'Created';
  const handleAddAttribute = () => {
    addStatusAttribute(categoryId, {
      id: `attr-${Date.now()}`,
      pcfAttribute: '',
      source: 'Constant',
      value: '',
    });
  };

  if (!category) {
    return (
      <CaptionText
        sx={{
          p: 1,
          textAlign: 'center',
          color: colors.text.secondary,
          display: 'block',
        }}
      >
        This status category is no longer available. Select another project phase status.
      </CaptionText>
    );
  }

  return (
    <WorkspaceSection
      title={category.name}
      titleHelp={mappingOverviewTooltips.statusCategoryMapping}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          width: 'min(280px, 100%)',
          px: 1,
          pt: 1,
          pb: 0.75,
        }}
      >
        <SearchableDropdown
          value={phaseKind}
          onChange={(value) =>
            updateStatusCategory(categoryId, {
              phaseKind: (value === 'Demolished' ? 'Demolished' : 'Created') as StatusPhaseKind,
            })
          }
          options={PHASE_KIND_OPTIONS}
          placeholder="Select Revit phase ownership…"
          ariaLabel="Revit phase property that owns this status category"
          sx={getScopeEditorDropdownSx(theme)}
        />
      </Box>
      <ScopeEditorTableVariant ariaLabel={`${category.name} downstream PCF status editor`} minWidth={560}>
        <MappingEditorHeader
          columns={[
            {
              key: 'pcfAttribute',
              width: COLUMN_WIDTHS.pcfAttribute,
              content: (
                <TableHeaderField
                  label="PCF status target"
                  value={filters.pcfAttribute}
                  onChange={(value) => handleFilterChange('pcfAttribute', value)}
                  ariaLabel="Filter PCF status attribute targets"
                  inputHeight={scopeEditorDensity.inputHeight}
                />
              ),
            },
            {
              key: 'source',
              width: COLUMN_WIDTHS.source,
              content: (
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                  Value source
                </Typography>
              ),
            },
            {
              key: 'value',
              width: COLUMN_WIDTHS.value,
              content: (
                <TableHeaderField
                  label="Direct value or Revit parameter"
                  value={filters.value}
                  onChange={(value) => handleFilterChange('value', value)}
                  ariaLabel="Filter status direct values or Revit parameters"
                  inputHeight={scopeEditorDensity.inputHeight}
                />
              ),
            },
            {
              key: 'actions',
              width: COLUMN_WIDTHS.actions,
              minWidth: COLUMN_WIDTHS.actions,
              align: 'center',
              content: (
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                  Mapping actions
                </Typography>
              ),
            },
          ]}
        />
        <TableBody>
          {visibleAttributes.map((attribute) => (
            <MappingRow key={attribute.id}>
              <TableCell sx={{ width: COLUMN_WIDTHS.pcfAttribute }}>
                <SearchableDropdown
                  value={attribute.pcfAttribute}
                  onChange={(value) =>
                    updateStatusAttribute(categoryId, attribute.id, { pcfAttribute: value })
                  }
                  options={PCF_ATTRIBUTE_OPTIONS}
                  placeholder="Select or enter PCF status attribute…"
                  freeSolo
                  ariaLabel={`PCF status attribute target for ${attribute.pcfAttribute || 'new status mapping'}`}
                  sx={getScopeEditorDropdownSx(theme)}
                />
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.source }}>
                <SearchableDropdown
                  value={attribute.source}
                  onChange={(value) =>
                    updateStatusAttribute(categoryId, attribute.id, {
                      source: (value === 'RevitParameter' ? 'RevitParameter' : 'Constant') as StatusValueSource,
                    })
                  }
                  options={SOURCE_OPTIONS}
                  placeholder="Select value source…"
                  ariaLabel={`Value source for ${attribute.pcfAttribute || 'new status mapping'}`}
                  sx={getScopeEditorDropdownSx(theme)}
                />
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.value }}>
                <SearchableDropdown
                  value={attribute.value}
                  onChange={(value) =>
                    updateStatusAttribute(categoryId, attribute.id, { value })
                  }
                  options={attribute.source === 'RevitParameter' ? revitParamOptions : []}
                  placeholder={
                    attribute.source === 'RevitParameter'
                      ? 'Select or enter Revit source parameter…'
                      : 'Enter direct PCF value…'
                  }
                  freeSolo
                  ariaLabel={`${attribute.pcfAttribute || 'Status'} direct value or Revit source parameter`}
                  sx={getScopeEditorDropdownSx(theme)}
                />
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.actions, textAlign: 'center' }}>
                <ActionHelpTooltip help={mappingOverviewTooltips.deleteStatusCategory}>
                  <IconButton
                    size="small"
                    onClick={() => removeStatusAttribute(categoryId, attribute.id)}
                    sx={getScopeEditorActionButtonSx(theme, 'danger')}
                    aria-label={`Delete ${attribute.pcfAttribute || 'attribute'}`}
                  >
                    <DeleteIcon sx={{ fontSize: '0.6875rem' }} />
                  </IconButton>
                </ActionHelpTooltip>
              </TableCell>
            </MappingRow>
          ))}
          <MappingRow>
            <TableCell colSpan={4} sx={{ py: 0.5 }}>
              <ActionHelpTooltip help={mappingOverviewTooltips.addStatusAttribute}>
                <CompactAddAction onClick={handleAddAttribute}>Add PCF status mapping</CompactAddAction>
              </ActionHelpTooltip>
            </TableCell>
          </MappingRow>
        </TableBody>
      </ScopeEditorTableVariant>
    </WorkspaceSection>
  );
};

export default StatusCategorySection;
